import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X } from "lucide-react";
import * as Sentry from "@sentry/react";
import { useCaptureContext, type CaptureType } from "@/contexts/CaptureContext";
import { useCollections } from "@/hooks/useCollections";
import { runCaptureSave } from "@/lib/captureProcessor";
import { resolvePromoPolicy } from "@/lib/promoPolicy";
import { useEntitlements, invalidateEntitlements } from "@/hooks/useEntitlements";
import { useCurrentRole } from "@/hooks/useCurrentRole";
import { invalidateMemories } from "@/hooks/useMemories";
import { useMemorySaved } from "@/hooks/useMemorySaved";
import { useDeferredIntent } from "@/contexts/DeferredIntentContext";
import { useCaptureSave } from "@/hooks/queries/useCaptureSave";
import { Button } from "@/components/ui/button";
import CaptureTypePicker from "@/components/capture/CaptureTypePicker";
import VoiceCapture from "@/components/capture/VoiceCapture";
import PhotoCapture, { type PhotoCaptureData } from "@/components/capture/PhotoCapture";
import ShortieCapture, { type ShortieCaptureData } from "@/components/capture/ShortieCapture";
import MoriChoiceGate from "@/components/capture/MoriChoiceGate";
import WaitScreen from "@/components/capture/WaitScreen";
import MemoryMetadataScreen from "@/components/MemoryMetadataScreen";
import MemoryDetailSheet from "@/components/MemoryDetailSheet";

/**
 * Orchestrates the unified 4-step capture pipeline.
 * Renders the appropriate full-screen overlay based on CaptureContext.step + data.type.
 */
export default function CaptureOrchestrator() {
  const { t } = useTranslation();
  const ctx = useCaptureContext();
  const { step, data, startCapture, selectType, completeCaptureStep, cancel, goBack, setProcessingMode, setPromoPolicy } = ctx;
  const { activeCollection } = useCollections();
  const { tier, maxPhotosPerMemory } = useEntitlements();
  const { role: currentRole } = useCurrentRole();
  const { onMemorySaved } = useMemorySaved();
  const { fire: fireDeferredIntent } = useDeferredIntent();
  const { saveDraft, uploadVoiceArtifacts, uploadContextAudio, isUploading } = useCaptureSave();
  const [detailMemoryId, setDetailMemoryId] = useState<string | null>(null);

  // Stashed by buildProcessor's processor; consumed once when AI processing
  // actually finishes. Splitting these from the inline await prevents
  // acorn / streak toasts from firing before the AI status flips to
  // processed/transcribed/skipped/failed.
  const pendingFanoutRef = useRef<{ memoryType: string; contentLibraryId: string | null } | null>(null);

  // Processor passed to WaitScreen — runs the save pipeline (insert + edge fn
  // kickoff). The streak / acorn / event fanout is deferred to
  // handleProcessingFinalized so users don't see "+N acorns" before the
  // memory's AI processing completes.
  const buildProcessor = useCallback(
    (captureData: typeof data) =>
      async (_adToken: string | null): Promise<string> => {
        const result = await runCaptureSave({
          data: captureData,
          activeCollection: activeCollection ?? null,
        });
        if (result.forcedSkip) {
          toast(t("memory.processing.savedWithoutAi"));
        }
        if (!result.skipFanout) {
          void invalidateMemories();
          void invalidateEntitlements();
          pendingFanoutRef.current = {
            memoryType: result.memoryType,
            contentLibraryId: result.contentLibraryId ?? null,
          };
        } else {
          pendingFanoutRef.current = null;
        }
        return result.memoryId;
      },
    [activeCollection, t],
  );

  const handleProcessingFinalized = useCallback(
    async (memoryId: string) => {
      const post = pendingFanoutRef.current;
      if (!post) return;
      pendingFanoutRef.current = null;
      try {
        await onMemorySaved(memoryId, post);
      } catch (err) {
        console.error("[CaptureOrchestrator] streak/acorn update failed (non-fatal):", err);
        Sentry.captureException(err, { tags: { flow: "memory-saved-streak" } });
      }
    },
    [onMemorySaved],
  );

  const handleDetailSheetClose = useCallback(() => {
    setDetailMemoryId(null);
    const intent = fireDeferredIntent();
    if (!intent) return;
    if (intent.kind === "capture_memory") {
      const cardContext = intent.payload?.cardContext;
      startCapture(
        cardContext
          ? { cardContext, contextPrompt: cardContext.body }
          : {},
      );
    }
    // Other intent kinds (open_upgrade / open_settings / open_mori_chat)
    // are home-screen routes; CaptureOrchestrator can't navigate without
    // pulling react-router in. Leaving them no-op for the in-app sheet
    // close path — the page-level MemoryDetail handler covers those.
  }, [fireDeferredIntent, startCapture]);

  // Resolve and store promoPolicy once when the artifact capture step begins.
  useEffect(() => {
    if (step !== "capture" || data.promoPolicy) return;
    const safeTier =
      tier === "keeper" || tier === "storyteller" ? tier : "seedling";
    const policy = resolvePromoPolicy({
      tier: safeTier,
      isChildCreator: currentRole === "child",
      isContributor: currentRole === "contributor",
    });
    setPromoPolicy(policy);
  }, [step, tier, currentRole, data.promoPolicy, setPromoPolicy]);

  if (step === "idle" || step === "done") {
    if (detailMemoryId) {
      return (
        <MemoryDetailSheet
          memoryId={detailMemoryId}
          isOpen
          onClose={handleDetailSheetClose}
        />
      );
    }
    return null;
  }

  // Capture flow accent: the collection color (from context or active-capture)
  // tints the primary CTA and decorative chrome. Falls back to the default
  // brand `bg-primary` when undefined.
  const accentColor = data.collectionColor ?? activeCollection?.color;

  // ── Shared "Finish Later" handler (inline on capture components) ─────────
  const handleFinishLater = async (
    artifact:
      | { kind: "voice"; audioBlob: Blob; audioDurationSeconds: number }
      | { kind: "photo"; imageFile: File }
      | { kind: "shortie"; videoBlob: Blob; thumbnailBlob?: Blob; videoDuration: number; videoMimeType: string },
  ) => {
    try {
      await saveDraft({
        artifact,
        memoryType: data.type,
        contextMode: data.contextMode,
        capsuleState: data.capsuleState,
      });
      toast.success(t("capture.decision.savedToast"));
      cancel();
    } catch (err) {
      console.error(err);
      toast.error(t("capture.decision.saveError"));
    }
  };

  // ── Step 1: Capture ──────────────────────────────────────────────────────

  if (step === "capture") {
    // No type selected yet → bottom sheet (nav + header remain visible above z-[43])
    if (!data.type) {
      return (
        <>
          {/* Dimmed backdrop — below nav (z-50) and top controls (z-40) */}
          <div
            className="fixed inset-0 z-[42] bg-black/30"
            onClick={cancel}
          />
          {/* Sheet anchored ABOVE the nav so content can't be occluded */}
          <div
            className="fixed left-0 right-0 z-[43] flex justify-center pointer-events-none"
            style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}
          >
            <div
              className="relative w-full max-w-[430px] bg-[#FDFBF7] rounded-t-3xl overflow-y-auto max-h-[85dvh] pointer-events-auto shadow-[0_-4px_24px_rgba(0,0,0,0.14)] pb-4"
            >
              <button
                type="button"
                onClick={cancel}
                className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" />
              </button>
              <CaptureTypePicker
                onSelect={(type: CaptureType) => {
                  selectType(type);
                }}
                cardContext={data.cardContext}
                onCardLeave={cancel}
                collectionId={data.collectionId}
                collectionName={data.contextPrompt}
                collectionIcon={data.collectionIcon}
                collectionColor={data.collectionColor}
              />
            </div>
          </div>
        </>
      );
    }

    // Uploading audio — brief spinner, no dismiss (upload is in flight)
    if (isUploading) {
      return (
        <Overlay>
          <div className="flex flex-col items-center gap-4 py-20">
            <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">{t("recording.upload.uploading")}</p>
          </div>
        </Overlay>
      );
    }

    // Type-specific capture UI
    switch (data.type) {
      case "voice_moment":
      case "general_memory":
      case "time_capsule":
        return (
          <Overlay onDismiss={cancel}>
            <VoiceCapture
              captureType={data.type as string}
              accentColor={accentColor}
              onComplete={async (result) => {
                const { audioBlob, audioDurationSeconds, writtenText, contextMode, contextBlob, contextText, approvalStatus } = result;
                // Written mode for general_memory — no audio to upload
                if (!audioBlob) {
                  completeCaptureStep({ writtenText, contextMode, approvalStatus });
                  return;
                }
                try {
                  const { audioStoragePath, contextAudioPath } = await uploadVoiceArtifacts({
                    audioBlob,
                    contextBlob,
                  });
                  completeCaptureStep({ audioStoragePath, audioDurationSeconds, contextMode, contextAudioPath, contextText, approvalStatus });
                } catch (err) {
                  console.error(err);
                  toast.error(t("recording.upload.error.uploadFailed"));
                }
              }}
              onFinishLater={data.type === "voice_moment"
                ? (result) => void handleFinishLater({ kind: "voice", audioBlob: result.audioBlob!, audioDurationSeconds: result.audioDurationSeconds ?? 0 })
                : undefined}
              onCancel={cancel}
              onBack={goBack}
              contextPrompt={data.contextPrompt}
              cardContext={data.cardContext ? { title: data.cardContext.title, body: data.cardContext.body } : null}
              voiceRequired={data.voiceRequired}
              autoStart={data.autoStartRecording}
            />
          </Overlay>
        );

      case "photo":
      case "drawing":
        return (
          <>
            <div className="fixed inset-0 z-[54] bg-black/40" onClick={cancel} />
            <div className="fixed bottom-0 left-0 right-0 z-[55] flex justify-center pointer-events-none">
              <div
                className="relative w-full max-w-[430px] bg-background rounded-t-3xl overflow-y-auto pointer-events-auto shadow-[0_-6px_32px_rgba(0,0,0,0.18)]"
                style={{ maxHeight: "90dvh", paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={cancel}
                  className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label={t("common.close")}
                >
                  <X className="h-4 w-4" />
                </button>
                <PhotoCapture
                  memoryType={data.type}
                  maxPhotos={data.type === "drawing" ? 1 : maxPhotosPerMemory}
                  accentColor={accentColor}
                  autoStart={data.autoStartRecording}
                  onSave={async (photoData: PhotoCaptureData) => {
                    let contextAudioPath: string | undefined;
                    if (photoData.contextBlob) {
                      try {
                        contextAudioPath = await uploadContextAudio(photoData.contextBlob);
                      } catch (err) {
                        console.error(err);
                        toast.error(t("recording.upload.error.uploadFailed"));
                      }
                    }
                    completeCaptureStep({
                      imageFile: photoData.imageFile,
                      imagePreviewUrl: photoData.imagePreviewUrl,
                      imageFiles: photoData.imageFiles,
                      imagePreviewUrls: photoData.imagePreviewUrls,
                      contextAudioPath,
                      contextText: photoData.contextText,
                      approvalStatus: photoData.approvalStatus,
                    });
                  }}
                  onFinishLater={(photoData) => void handleFinishLater({ kind: "photo", imageFile: photoData.imageFile })}
                  onCancel={cancel}
                />
              </div>
            </div>
          </>
        );

      case "shortie":
        return (
          <Overlay tall onDismiss={cancel}>
            <ShortieCapture
              onSave={async (shortieData: ShortieCaptureData) => {
                let contextAudioPath: string | undefined;
                if (shortieData.contextBlob) {
                  try {
                    contextAudioPath = await uploadContextAudio(shortieData.contextBlob);
                  } catch (err) {
                    console.error(err);
                  }
                }
                completeCaptureStep({
                  videoBlob: shortieData.videoBlob,
                  thumbnailBlob: shortieData.thumbnailBlob,
                  videoDuration: shortieData.durationSeconds,
                  videoMimeType: shortieData.mimeType,
                  contextAudioPath,
                  contextText: shortieData.contextText,
                  approvalStatus: shortieData.approvalStatus,
                });
              }}
              onFinishLater={(shortieData) => void handleFinishLater({ kind: "shortie", videoBlob: shortieData.videoBlob, thumbnailBlob: shortieData.thumbnailBlob, videoDuration: shortieData.durationSeconds, videoMimeType: shortieData.mimeType })}
              onCancel={cancel}
            />
          </Overlay>
        );

      default:
        return null;
    }
  }

  // ── Step 2: Metadata ─────────────────────────────────────────────────────
  if (step === "metadata") {
    return (
      <Overlay onDismiss={cancel}>
        <MemoryMetadataScreen />
      </Overlay>
    );
  }

  // ── Step 3: Choice gate ──────────────────────────────────────────────────
  if (step === "choice_gate") {
    return (
      <Overlay onDismiss={cancel}>
        <MoriChoiceGate
          hasAudio={!!data.audioStoragePath}
          onChoose={setProcessingMode}
        />
      </Overlay>
    );
  }

  // ── Step 4: Processing ───────────────────────────────────────────────────
  // WaitScreen overlays the full viewport via `fixed inset-0 z-[60]`. Still
  // portaled into `#capture-overlay-host` to keep the React subtree colocated
  // with the shell; falls back to inline render if the host isn't in the DOM.
  if (step === "processing") {
    const host = typeof document !== "undefined"
      ? document.getElementById("capture-overlay-host")
      : null;
    const waitScreen = (
      <WaitScreen
        processingMode={data.processingMode}
        promoPolicy={data.promoPolicy}
        processor={buildProcessor(data)}
        onProcessingFinalized={handleProcessingFinalized}
        onComplete={(memoryId) => {
          if (memoryId) setDetailMemoryId(memoryId);
          cancel();
        }}
        onClose={cancel}
      />
    );
    return host ? createPortal(waitScreen, host) : waitScreen;
  }

  return null;
}

// ── Bottom-sheet capture wrapper ───────────────────────────────────────────
//
// Leaves a slim dimmed strip at the top (~10dvh) — tapping it dismisses.
// The sheet sits above the nav (z-[56]) so nothing leaks through.

function Overlay({
  children,
  tall,
  onDismiss,
}: {
  children: React.ReactNode;
  tall?: boolean;
  onDismiss?: () => void;
}) {
  const sheetHeight = tall ? "95dvh" : "90dvh";

  return (
    <>
      {/* Dimmed backdrop — tapping the visible top strip dismisses */}
      <div
        className="fixed inset-0 z-[54] bg-black/40"
        onClick={onDismiss}
      />

      {/* Sheet — anchored to bottom, above nav */}
      <div className="fixed bottom-0 left-0 right-0 z-[55] flex justify-center pointer-events-none">
        <div
          className="relative w-full max-w-[430px] bg-background rounded-t-3xl pointer-events-auto shadow-[0_-6px_32px_rgba(0,0,0,0.18)] flex flex-col overflow-hidden"
          style={{ maxHeight: sheetHeight }}
          onClick={(e) => e.stopPropagation()}
        >
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center py-6">
            {children}
          </div>
        </div>
      </div>
    </>
  );
}
